/* =============================================================
   Lookbook Carousel — Flickity-powered Custom Element
   
   Script execution order is GUARANTEED by the browser/importmap:
   Flickity is loaded globally in theme.liquid via vendor-scripts-v11.js.
   
   Layout strategy:
   · Mobile  (<769px): always use Flickity when cells > 1
   · Desktop (≥769px): use Flickity only when track overflows its container
   ============================================================= */
import { debounce } from 'util.misc';

if (!customElements.get('kit-lookbook-carousel')) {
  const MOBILE_BP = 769;

  class KitLookbookCarousel extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.container = this;
      this.track     = this.querySelector('[data-lookbook-track]');
      this.navEl     = this.querySelector('[data-lookbook-nav]');
      this.prevBtn   = this.querySelector('[data-lookbook-prev]');
      this.nextBtn   = this.querySelector('[data-lookbook-next]');
      this.flkty     = null;

      if (!this.track) return;
      this._init();

      // Dynamically load and initialize look-set-drawer
      this.initLookSetDrawers();
    }

    disconnectedCallback() {
      this.destroy();
    }

    async initLookSetDrawers() {
      try {
        const module = await import('element.look-set-drawer');
        if (module && typeof module.initLookSetDrawers === 'function') {
          module.initLookSetDrawers(this.dataset.sectionId, this);
        }
      } catch (err) {
        console.warn('Failed to load look-set-drawer module dynamically', err);
      }
    }

    _init() {
      var self = this;

      function schedule(fn) {
        if (document.readyState === 'complete') {
          requestAnimationFrame(fn);
        } else {
          window.addEventListener('load', function () {
            requestAnimationFrame(fn);
          }, { once: true });
        }
      }

      schedule(function () { self._checkLayout(); });
      this._initMediaObserver();

      this._onResize = debounce(250, function () {
        requestAnimationFrame(function () { self._checkLayout(); });
      });
      window.addEventListener('resize', this._onResize);
    }

    /* ── Layout detection ──────────────────────────────────── */
    _checkLayout() {
      var cells = this.track.querySelectorAll('.lookbook-carousel__cell');
      if (!cells.length) return;

      var isMobile    = window.innerWidth < MOBILE_BP;
      var needsSlider;

      if (isMobile) {
        needsSlider = cells.length > 1;
      } else {
        var contentW   = this.track.scrollWidth;
        var containerW = this.track.parentElement.offsetWidth;
        needsSlider    = contentW > containerW + 2; // 2px rounding buffer
      }

      if (needsSlider && !this.flkty) {
        this._activate();
      } else if (!needsSlider && this.flkty) {
        this._deactivate();
      }
    }

    /* ── Flickity lifecycle ────────────────────────────────── */
    _activate() {
      var self = this;

      if (this.navEl) this.navEl.style.display = 'flex';

      this.flkty = new Flickity(this.track, {
        cellAlign:       'left',
        contain:         true,
        wrapAround:      false,
        groupCells:      false,
        prevNextButtons: false,
        pageDots:        false,
        draggable:       '>1',
        dragThreshold:   6,
        percentPosition: false,
        setGallerySize:  true,
        rightToLeft:     !!(typeof theme !== 'undefined' && theme.config && theme.config.rtl)
      });

      // Wire custom nav buttons
      if (this.prevBtn) {
        this._onPrev = function () { self.flkty.previous(); };
        this.prevBtn.addEventListener('click', this._onPrev);
      }
      if (this.nextBtn) {
        this._onNext = function () { self.flkty.next(); };
        this.nextBtn.addEventListener('click', this._onNext);
      }

      // Keep button states in sync
      this._onSelect = function () { self._syncNav(); };
      this.flkty.on('select', this._onSelect);
      this.flkty.on('ready',  this._onSelect);
    }

    _deactivate() {
      if (this.navEl) this.navEl.style.display = 'none';
      if (this.flkty) {
        this.flkty.destroy();
        this.flkty = null;
      }
    }

    /* ── Prev/Next disabled state ──────────────────────────── */
    _syncNav() {
      if (!this.flkty) return;
      var idx   = this.flkty.selectedIndex;
      var last  = (this.flkty.slides || []).length - 1;

      this._setBtn(this.prevBtn, idx <= 0);
      this._setBtn(this.nextBtn, idx >= last || last < 0);
    }

    _setBtn(btn, isDisabled) {
      if (!btn) return;
      btn.disabled = isDisabled;
      btn.classList.toggle('is-disabled', isDisabled);
    }

    /* ── Media Observer ──────────────────────────────────────── */
    _initMediaObserver() {
      var self = this;
      var cells = this.container.querySelectorAll('.lookbook-carousel__cell');
      if (!cells.length) return;

      if (!('IntersectionObserver' in window)) {
        // Fallback for older browsers: show immediately
        cells.forEach(function (cell) {
          self._revealCellMedia(cell);
        });
        return;
      }

      var observerOptions = {
        root: null,
        rootMargin: '0px 0px 200px 0px',
        threshold: 0.01
      };

      this.mediaObserver = new IntersectionObserver(function (entries, observer) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            var cell = entry.target;
            self._revealCellMedia(cell);
            observer.unobserve(cell);
          }
        });
      }, observerOptions);

      cells.forEach(function (cell) {
        var img = cell.querySelector('img');
        if (img) {
          img.style.opacity = '0';
          img.style.transition = 'opacity 0.5s ease, transform 0.55s cubic-bezier(0.16, 1, 0.3, 1)';
        }
        self.mediaObserver.observe(cell);
      });
    }

    _revealCellMedia(cell) {
      var img = cell.querySelector('img');
      if (!img) return;

      function showImage() {
        img.style.opacity = '1';
        cell.classList.add('is-loaded');
      }

      if (img.complete) {
        showImage();
      } else {
        img.addEventListener('load', showImage);
        img.addEventListener('error', showImage);
      }
    }

    /* ── Teardown ──────────────────────────────────────────── */
    destroy() {
      window.removeEventListener('resize', this._onResize);
      if (this.mediaObserver) {
        this.mediaObserver.disconnect();
        this.mediaObserver = null;
      }
      if (this.prevBtn && this._onPrev)
        this.prevBtn.removeEventListener('click', this._onPrev);
      if (this.nextBtn && this._onNext)
        this.nextBtn.removeEventListener('click', this._onNext);
      if (this.flkty) {
        this.flkty.off('select', this._onSelect);
        this.flkty.off('ready',  this._onSelect);
        this.flkty.destroy();
        this.flkty = null;
      }
    }
  }

  customElements.define('kit-lookbook-carousel', KitLookbookCarousel);
}